/**
 * FunnelChart Component
 * 
 * Displays form completion funnel visualization using Recharts.
 * Shows step-by-step drop-off rates for form completion.
 */

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import type { FunnelData, FunnelStep } from '@formflow/shared';
import './FunnelChart.css';

interface FunnelChartProps {
  data: FunnelData | null;
  loading?: boolean;
}

interface ChartDataPoint {
  name: string;
  views: number;
  completions: number;
  dropOffRate: number;
  stepIndex: number;
}

// Custom tooltip component
interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: ChartDataPoint;
    value: number;
    name: string;
    color: string;
  }>;
  label?: string;
}

function CustomTooltip({ active, payload }: TooltipProps) {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;
  
  return (
    <div className="funnel-tooltip">
      <div className="tooltip-title">{data.name}</div>
      <div className="tooltip-row">
        <span className="tooltip-label">Views:</span>
        <span className="tooltip-value">{data.views.toLocaleString()}</span>
      </div>
      <div className="tooltip-row">
        <span className="tooltip-label">Completions:</span>
        <span className="tooltip-value">{data.completions.toLocaleString()}</span>
      </div>
      <div className="tooltip-row">
        <span className="tooltip-label">Drop-off Rate:</span>
        <span className="tooltip-value drop-off">{data.dropOffRate.toFixed(1)}%</span>
      </div>
    </div>
  );
}

// Custom label for bar - using explicit any for Recharts compatibility
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function renderCustomLabel(props: any) {
  const x = typeof props.x === 'number' ? props.x : 0;
  const y = typeof props.y === 'number' ? props.y : 0;
  const width = typeof props.width === 'number' ? props.width : 0;
  const value = typeof props.value === 'number' ? props.value : 0;
  
  return (
    <text
      x={x + width / 2}
      y={y - 10}
      fill="var(--formflu-text)"
      textAnchor="middle"
      fontSize={12}
      fontWeight={600}
    >
      {value.toLocaleString()}
    </text>
  );
}

// Generate color based on drop-off rate
function getBarColor(dropOffRate: number): string {
  if (dropOffRate <= 10) return '#22C55E'; // Green - good
  if (dropOffRate <= 25) return '#1FB6A6'; // Teal - okay
  if (dropOffRate <= 50) return '#F59E0B'; // Warning - needs attention
  return '#EF4444'; // Red - high drop-off
}

export function FunnelChart({ data, loading }: FunnelChartProps) {
  if (loading) {
    return (
      <div className="funnel-chart-container">
        <div className="funnel-chart-header">
          <h3 className="funnel-chart-title">Completion Funnel</h3>
        </div>
        <div className="funnel-chart-loading">
          <div className="loading-skeleton chart-skeleton" />
        </div>
      </div>
    );
  }

  if (!data || !data.steps || data.steps.length === 0) {
    return (
      <div className="funnel-chart-container">
        <div className="funnel-chart-header">
          <h3 className="funnel-chart-title">Completion Funnel</h3>
        </div>
        <div className="funnel-chart-empty">
          <span className="empty-icon">📊</span>
          <p>No funnel data available for this period</p>
          <p className="empty-hint">Data will appear once visitors interact with your form</p>
        </div>
      </div>
    );
  }

  // Transform data for the chart
  const chartData: ChartDataPoint[] = data.steps.map((step: FunnelStep) => ({
    name: step.step_name,
    views: step.views,
    completions: step.completions,
    dropOffRate: step.drop_off_rate,
    stepIndex: step.step_index,
  }));

  // Calculate overall stats
  const totalViews = chartData[0]?.views || 0;
  const totalCompletions = chartData[chartData.length - 1]?.completions || 0;
  const overallConversion = totalViews > 0 
    ? ((totalCompletions / totalViews) * 100).toFixed(1) 
    : '0';

  return (
    <div className="funnel-chart-container">
      <div className="funnel-chart-header">
        <div>
          <h3 className="funnel-chart-title">Completion Funnel</h3>
          <p className="funnel-chart-subtitle">
            {totalViews.toLocaleString()} views → {totalCompletions.toLocaleString()} completions ({overallConversion}% conversion)
          </p>
        </div>
        <div className="funnel-legend">
          <div className="legend-item">
            <span className="legend-color" style={{ background: '#22C55E' }} />
            <span>≤10% drop-off</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ background: '#F59E0B' }} />
            <span>11-50%</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ background: '#EF4444' }} />
            <span>&gt;50%</span>
          </div>
        </div>
      </div>

      <div className="funnel-chart-body">
        <ResponsiveContainer width="100%" height={350}>
          <BarChart
            data={chartData}
            margin={{ top: 30, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke="var(--formflu-border)" 
              vertical={false}
            />
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: 12, fill: 'var(--formflu-text-light)' }}
              tickLine={false}
              axisLine={{ stroke: 'var(--formflu-border)' }}
            />
            <YAxis 
              tick={{ fontSize: 12, fill: 'var(--formflu-text-light)' }}
              tickLine={false}
              axisLine={{ stroke: 'var(--formflu-border)' }}
              tickFormatter={(value) => value.toLocaleString()}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar 
              dataKey="completions" 
              radius={[4, 4, 0, 0]}
              maxBarSize={80}
            >
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={getBarColor(entry.dropOffRate)}
                />
              ))}
              <LabelList 
                dataKey="completions" 
                position="top" 
                content={renderCustomLabel}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Step Details Table */}
      <div className="funnel-details">
        <table className="funnel-table">
          <thead>
            <tr>
              <th>Step</th>
              <th>Views</th>
              <th>Completions</th>
              <th>Drop-off</th>
              <th>Avg. Time</th>
            </tr>
          </thead>
          <tbody>
            {data.steps.map((step: FunnelStep) => (
              <tr key={step.step_id}>
                <td>
                  <span className="step-number">{step.step_index + 1}</span>
                  {step.step_name}
                </td>
                <td>{step.views.toLocaleString()}</td>
                <td>{step.completions.toLocaleString()}</td>
                <td>
                  <span className={`drop-off-badge ${getDropOffClass(step.drop_off_rate)}`}>
                    {step.drop_off_rate.toFixed(1)}%
                  </span>
                </td>
                <td>
                  {step.avg_time_seconds 
                    ? formatTime(step.avg_time_seconds)
                    : '—'
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function getDropOffClass(rate: number): string {
  if (rate <= 10) return 'low';
  if (rate <= 25) return 'medium';
  if (rate <= 50) return 'high';
  return 'critical';
}

function formatTime(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
}

export default FunnelChart;
